import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";

import { colors } from "@zapalacorp/tokens";
import ConfirmationModal from "Components/Dialogs/ConfirmationModal";
import { useDespatchActions } from "../../views/DiaryResourcesView/hooks/useDespatchActions";
import { Color } from "Components/Form/Button";
import { DespatchCrew } from "Models/despatch.crew";
import { DespatchTask } from "Models/despatch.task";
import { Task } from "Models/task";
import { useTranslation } from "react-i18next";
import { PiWarning } from "react-icons/pi";
import { getMuiPopupContainer } from "Themes/dispatchTheme";

import { ConfirmationModalBody, ConfirmationModalBodyText, useStyles } from "./styles";

type Props = {
  onSuccessDeletePlaceholderTask: (taskId: number) => void;
};
export type DespatchDeletePlaceholderTaskModalHandles = {
  open: (task: DespatchTask | Task, crew?: DespatchCrew) => void;
  close: () => void;
};

const DespatchDeletePlaceholderTaskComponent: ForwardRefRenderFunction<
  DespatchDeletePlaceholderTaskModalHandles,
  Props
> = ({ onSuccessDeletePlaceholderTask }, ref) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [task, setTask] = useState<DespatchTask | Task | null | undefined>(null);
  const [isOpen, setIsOpen] = useState(false);

  const { useDeletePlaceholderTask } = useDespatchActions();
  const { mutate: deletePlaceholderlTask, isLoading } = useDeletePlaceholderTask((taskId) =>
    onSuccessDeletePlaceholderTask(taskId)
  );

  const onOpenModal = useCallback((dataTask: DespatchTask | Task) => {
    setTask(dataTask);
    setIsOpen(true);
  }, []);

  const onCloseModal = useCallback(() => {
    setTask(null);
    setIsOpen(false);
  }, []);

  const onSubmit = () => {
    if (task?.id) {
      deletePlaceholderlTask(task.id, {
        onSuccess: () => {
          onCloseModal();
        },
      });
    }
  };

  useImperativeHandle(ref, () => ({
    open: onOpenModal,
    close: onCloseModal,
  }));

  return (
    <ConfirmationModal
      container={getMuiPopupContainer}
      open={isOpen}
      title={translate("deleteTask")}
      closeModal={() => onCloseModal()}
      leftIconTitle={<PiWarning color={colors.error600} />}
      loading={isLoading}
      submit={onSubmit}
      textSubmit={translate("yesDelete")}
      textCancel={translate("noKeepIt")}
      customColorCancel={Color.TEXT_INFO}
      customColorSave={Color.ERROR}
      type="error"
      maxWidth="520px"
      modalFooterCustomClass={classes.modalFooter}
      showCancelButton
    >
      <ConfirmationModalBody>
        <ConfirmationModalBodyText>
          {translate("areYouSureyouWantToDeleteTheTask")} {task?.name}?
        </ConfirmationModalBodyText>
        <ConfirmationModalBodyText isError>
          {translate("thisActionCannotBeUndone")}!
        </ConfirmationModalBodyText>
      </ConfirmationModalBody>
    </ConfirmationModal>
  );
};

const DespatchDeletePlaceholderTask = forwardRef(DespatchDeletePlaceholderTaskComponent);

export { DespatchDeletePlaceholderTask };
